import { API_ENDPOINTS, apiService } from "../api";
import { mockSearchWorkspaceUsers } from "./workspaceMembersApi.mock";
import {
  mapMemberInvitesToApi,
  mapWorkspaceUserSearchResponse,
} from "./workspaceMembersMappers";
import type {
  WorkspaceMemberInvite,
  WorkspaceUserSearchQuery,
  WorkspaceUserSearchResponse,
  WorkspaceUserSearchResponseApi,
} from "./workspaceMembers.types";
import { isMockApiFlag } from "../apiMode";

/** Defaults to mock unless explicitly set to `live`. */
const isMockWorkspaceMembersApiEnabled = (): boolean =>
  // process.env.REACT_APP_WORKSPACE_MEMBERS_API !== "live";
  isMockApiFlag(process.env.REACT_APP_WORKSPACE_MEMBERS_API);

/** True only when `npm start` is running — not Jest (`test`) or production builds. */
const isLocalDevRuntime = (): boolean => process.env.NODE_ENV === "development";

const searchWithMock = async (
  query: WorkspaceUserSearchQuery,
): Promise<WorkspaceUserSearchResponse> =>
  mapWorkspaceUserSearchResponse(await mockSearchWorkspaceUsers(query));

const buildUserSearchUrl = (query: WorkspaceUserSearchQuery): string => {
  const params = new URLSearchParams();
  params.set("q", query.q);
  if (query.limit != null) params.set("limit", String(query.limit));
  return `${API_ENDPOINTS.users.search}?${params.toString()}`;
};

export const searchWorkspaceUsers = async (
  query: WorkspaceUserSearchQuery,
): Promise<WorkspaceUserSearchResponse> => {
  if (isMockWorkspaceMembersApiEnabled()) {
    return searchWithMock(query);
  }

  try {
    const api = await apiService.get<WorkspaceUserSearchResponseApi>(
      buildUserSearchUrl(query),
    );
    const mapped = mapWorkspaceUserSearchResponse(api);
    const hasQuery = query.q.trim().length >= 2;
    if (isLocalDevRuntime() && hasQuery && mapped.items.length === 0) {
      return searchWithMock(query);
    }
    return mapped;
  } catch (error) {
    if (isLocalDevRuntime()) {
      return searchWithMock(query);
    }
    throw error;
  }
};

export const __buildUserSearchUrl_forTests = buildUserSearchUrl;

/** POST /api/v1/workspaces/{id}/members — add members one at a time (backend accepts flat {user_id, role}). */
export const addWorkspaceMembers = async (
  workspaceId: string,
  invites: WorkspaceMemberInvite[],
): Promise<void> => {
  const payload = mapMemberInvitesToApi(invites);
  await Promise.all(
    payload.map((item) =>
      apiService.post(API_ENDPOINTS.workspaces.members(workspaceId), {
        user_id: item.user_id,
        role: item.role,
      }),
    ),
  );
};
